import { useEffect, useMemo, useState } from "react";
import { JEDI_HR_URL } from "../../constants";
import axiosClient from "../../utils/axiosClient";
import "./StorePass.css";

const BASE_URL = "https://" + JEDI_HR_URL;
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "-";
const locType = (loc) =>
  String(loc).length <= 3 ? "DC" : String(loc).length === 4 ? "Store" : "Office";

const MAX_PASSES = 4;
const CLOSED = ["E", "C"];

const passState = (p) => {
  if (p.status === "C") return { pill: "CANCELLED", cls: "sp-pill-expired" };
  if (p.status === "E") return { pill: "EXPIRED", cls: "sp-pill-expired" };
  const devices = Object.values(p.device_ips || {});
  const synced = devices.filter((s) => s === "success" || s === "skipped").length;
  if (p.status === "F") return { pill: "WAITING FOR DEVICE", cls: "sp-pill-syncing", synced, total: devices.length };
  if (synced < devices.length) return { pill: "PARTIAL SYNC", cls: "sp-pill-syncing", synced, total: devices.length };
  return { pill: "ACTIVE", cls: "sp-pill-active", synced, total: devices.length };
};

const StorePass = () => {
  const [passes, setPasses] = useState([]);
  // validity of the next pass, from the API: 7 days, or 30 while the employee's own
  // store has not opened yet (new-launch teams training at nearby stores)
  const [passDays, setPassDays] = useState(7);
  const [locs, setLocs] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedLoc, setSelectedLoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const loadPasses = () =>
    axiosClient
      .post(BASE_URL + "/store_pass/list", {})
      .then((r) => {
        setPasses(r?.data?.data || []);
        setPassDays(Number(r?.data?.pass_days) || 7);
      })
      .catch(console.error);

  useEffect(() => {
    loadPasses();
    axiosClient
      .post(BASE_URL + "/store_pass/locs", {})
      .then((r) => setLocs(r?.data?.data || []))
      .catch(console.error);
  }, []);

  const active = passes.filter((p) => !CLOSED.includes(p.status));
  const past = passes.filter((p) => CLOSED.includes(p.status));

  const cancelPass = (passId) => {
    if (cancelling) return;
    setCancelling(true);
    setNotice(null);
    axiosClient
      .post(BASE_URL + "/store_pass/cancel", { pass_id: passId })
      .then((r) => {
        if (r?.data?.status === "success") {
          setConfirmId(null);
          setNotice({ ok: true, text: r?.data?.message || "Pass cancelled" });
          loadPasses();
        } else {
          setNotice({ ok: false, text: r?.data?.error_message || "Could not cancel the pass" });
        }
      })
      .catch((e) => setNotice({ ok: false, text: e?.message || "Could not cancel the pass" }))
      .finally(() => setCancelling(false));
  };

  const filteredLocs = useMemo(
    () =>
      locs.filter(
        (l) =>
          !search ||
          l.loc_name?.toLowerCase().includes(search.toLowerCase()) ||
          String(l.loc).includes(search),
      ),
    [locs, search],
  );

  const validTill = new Date();
  validTill.setDate(validTill.getDate() + passDays);

  const generatePass = () => {
    if (!selectedLoc || submitting) return;
    setSubmitting(true);
    setError("");
    axiosClient
      .post(BASE_URL + "/store_pass/create", { loc: selectedLoc.loc })
      .then((r) => {
        if (r?.data?.status === "success") {
          closeModal();
          loadPasses();
        } else {
          setError(r?.data?.error_message || "Could not generate pass");
        }
      })
      .catch((e) => setError(e?.message || "Could not generate pass"))
      .finally(() => setSubmitting(false));
  };

  const closeModal = () => {
    setModalOpen(false);
    setSearch("");
    setSelectedLoc(null);
    setError("");
  };

  const PassCard = ({ pass }) => {
    const st = passState(pass);
    const daysLeft = Math.max(
      0,
      Math.ceil((new Date(pass.valid_till) - new Date()) / 86400000),
    );
    const closed = CLOSED.includes(pass.status);
    return (
      <div className={`sp-pass-card ${closed ? "sp-expired" : ""}`}>
        <div className="sp-loc-badge">
          <div className="sp-loc-code">{pass.loc}</div>
          <div className="sp-loc-type">{locType(pass.loc)}</div>
        </div>
        <div className="sp-pass-main">
          <div className="sp-pass-loc-name">{pass.loc_name || pass.loc}</div>
          <div className="sp-pass-meta">
            <span>
              Valid{" "}
              <span className="sp-mono">
                {fmtDate(pass.valid_from)} - {fmtDate(pass.valid_till)}
              </span>
            </span>
            {!closed && (
              <span>
                Devices synced{" "}
                <span className="sp-mono">
                  {st.synced}/{st.total}
                </span>
                {st.synced < st.total &&
                  " - the device is offline, the sync retries on its own every night. No need to apply again."}
              </span>
            )}
          </div>
        </div>
        <div className="sp-pass-right">
          <span className={`sp-pill ${st.cls}`}>{st.pill}</span>
          {!closed && (
            <span className="sp-expires-in">
              expires in <b>{daysLeft} day{daysLeft !== 1 ? "s" : ""}</b>
            </span>
          )}
          {!closed &&
            (confirmId === pass.pass_id ? (
              <span className="sp-cancel-row">
                <button
                  className="sp-link-danger"
                  disabled={cancelling}
                  onClick={() => cancelPass(pass.pass_id)}
                >
                  {cancelling ? "Cancelling..." : "Confirm cancel"}
                </button>
                <button className="sp-link-muted" onClick={() => setConfirmId(null)}>
                  Keep
                </button>
              </span>
            ) : (
              <button
                className="sp-link-muted"
                onClick={() => {
                  setNotice(null);
                  setConfirmId(pass.pass_id);
                }}
              >
                Cancel pass
              </button>
            ))}
        </div>
      </div>
    );
  };

  return (
    <div className="sp-page">
      <div className="sp-page-head">
        <div>
          <div className="sp-page-title">Store Pass</div>
          <div className="sp-page-sub">
            Temporary biometric access at another More location - valid {passDays} days,
            auto-expires on the device
          </div>
        </div>
        <button className="sp-btn-primary" onClick={() => setModalOpen(true)}>
          + New Store Pass
        </button>
      </div>

      {notice && (
        <div className={notice.ok ? "sp-notice" : "sp-error"}>{notice.text}</div>
      )}

      <div className="sp-section-label">
        Active <span className="sp-section-count">{active.length}</span>
      </div>
      {active.map((p) => (
        <PassCard key={p.pass_id} pass={p} />
      ))}
      {active.length === 0 && (
        <div className="sp-empty-hint">No active passes.</div>
      )}

      {past.length > 0 && (
        <>
          <div className="sp-section-label">
            Past <span className="sp-section-count">{past.length}</span>
          </div>
          {past.map((p) => (
            <PassCard key={p.pass_id} pass={p} />
          ))}
        </>
      )}

      <div className="sp-empty-hint">
        You can hold up to {MAX_PASSES} active passes at a time. Passes are
        deleted from the device after expiry, or immediately if you cancel one.
      </div>

      {modalOpen && (
        <div className="sp-modal-overlay" onClick={closeModal}>
          <div className="sp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sp-modal-title">New Store Pass</div>
            <div className="sp-modal-sub">
              Your face will be synced to the selected location's TruID device
              for {passDays} days.
            </div>

            <div className="sp-field">
              <label className="sp-field-label">Destination location</label>
              {selectedLoc ? (
                <div className="sp-field-input" onClick={() => setSelectedLoc(null)}>
                  <span className="sp-mono">{selectedLoc.loc}</span>
                  {selectedLoc.loc_name}
                </div>
              ) : (
                <>
                  <input
                    className="sp-field-input"
                    placeholder="Search by store name or code"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                  />
                  <div className="sp-loc-drop">
                    {filteredLocs.slice(0, 6).map((l) => (
                      <div
                        key={l.loc}
                        className="sp-loc-opt"
                        onClick={() => setSelectedLoc(l)}
                      >
                        <span className="sp-mono">{l.loc}</span>
                        {l.loc_name}
                        <span className="sp-type-tag">{locType(l.loc)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="sp-field-row">
              <div className="sp-field">
                <label className="sp-field-label">Valid from</label>
                <div className="sp-field-input sp-readonly sp-mono">
                  {fmtDate(new Date())}
                </div>
              </div>
              <div className="sp-field">
                <label className="sp-field-label">Valid till</label>
                <div className="sp-field-input sp-readonly sp-mono">
                  {fmtDate(validTill)}
                </div>
              </div>
            </div>

            <div className="sp-info-callout">
              Pass is active within ~1 minute. Access ends automatically on{" "}
              <b>{fmtDate(validTill)}</b> - no action needed from you.
            </div>
            <div className="sp-cap-note">
              You can hold up to {MAX_PASSES} active passes. Generating a pass
              for your own store is not needed - you are already synced there.
            </div>

            {error && <div className="sp-error">{error}</div>}

            <div className="sp-modal-actions">
              <button className="sp-btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button
                className="sp-btn-primary"
                disabled={!selectedLoc || submitting}
                onClick={generatePass}
              >
                {submitting ? "Generating..." : "Generate Pass"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default StorePass;
